// Create / edit a client portal: slug, branding, visible reports, status.

import { useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    FormLabel,
    Grid,
    InputAdornment,
    Switch,
    TextField,
    Typography,
} from '@mui/material'

import { useCreatePortalMutation, useUpdatePortalMutation } from '@/apis/v2Api'
import {
    DEFAULT_PORTAL_BRANDING,
    DEFAULT_PORTAL_REPORTS,
    PORTAL_REPORTS,
    PORTAL_REPORT_LABELS,
    isEmail,
    isHttpsUrl,
    isValidPortalSlug,
    slugify,
    toggleReport,
    type Portal,
    type PortalBranding,
} from '@/apis/portalTypes'
import { useAppDispatch } from '@/app/hooks'
import { showNotification } from '@/app/notificationSlice'
import { useProjectId } from '@/hooks/useReportArgs'
import { contrastText, normalizeHex } from '@/modules/portal/portalTheme'

import { ApiErrorAlert } from './ApiErrorAlert'

function ColorField({
    label,
    value,
    onChange,
}: {
    label: string
    value: string
    onChange: (v: string) => void
}) {
    const [text, setText] = useState(value)
    return (
        <TextField
            size="small"
            label={label}
            value={text}
            onChange={e => {
                setText(e.target.value)
                if (/^#[0-9a-fA-F]{6}$/.test(e.target.value)) {
                    onChange(e.target.value.toLowerCase())
                }
            }}
            error={!/^#[0-9a-fA-F]{6}$/.test(text)}
            slotProps={{
                input: {
                    startAdornment: (
                        <InputAdornment position="start">
                            <Box
                                component="input"
                                type="color"
                                value={value}
                                aria-label={`${label} picker`}
                                onChange={e => {
                                    const v = (e.target as HTMLInputElement).value.toLowerCase()
                                    setText(v)
                                    onChange(v)
                                }}
                                sx={{
                                    width: 28,
                                    height: 28,
                                    p: 0,
                                    border: 'none',
                                    bgcolor: 'transparent',
                                    cursor: 'pointer',
                                }}
                            />
                        </InputAdornment>
                    ),
                },
            }}
        />
    )
}

/** Mini header preview: brand colors + logo / product name. */
function BrandPreview({ branding }: { branding: PortalBranding }) {
    const primary = normalizeHex(branding.primaryColor, DEFAULT_PORTAL_BRANDING.primaryColor)
    const accent = normalizeHex(branding.accentColor, DEFAULT_PORTAL_BRANDING.accentColor)
    const logoOk = !!branding.logoUrl && isHttpsUrl(branding.logoUrl)
    return (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            <Box
                sx={{
                    bgcolor: primary,
                    color: contrastText(primary),
                    px: 2,
                    py: 1.25,
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 52,
                }}
            >
                {logoOk ? (
                    <Box
                        component="img"
                        src={branding.logoUrl ?? ''}
                        alt="Logo preview"
                        sx={{ maxHeight: 32, maxWidth: 180, objectFit: 'contain' }}
                    />
                ) : (
                    <Typography fontWeight={700}>{branding.productName || 'Analytics'}</Typography>
                )}
            </Box>
            <Box sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'flex-end', height: 56 }}>
                {[0.4, 0.7, 0.55, 0.9, 0.65, 1].map((h, i) => (
                    <Box
                        key={i}
                        sx={{
                            flex: 1,
                            height: `${h * 100}%`,
                            bgcolor: i % 2 ? accent : primary,
                            borderRadius: 0.5,
                        }}
                    />
                ))}
            </Box>
        </Box>
    )
}

interface Props {
    portal: Portal | null
    canRemoveBranding: boolean
    onClose: () => void
    onSaved: (p: Portal) => void
}

export function PortalEditorDialog({ portal, canRemoveBranding, onClose, onSaved }: Props) {
    const projectId = useProjectId()
    const dispatch = useAppDispatch()
    const editing = !!portal
    const [createPortal, createState] = useCreatePortalMutation()
    const [updatePortal, updateState] = useUpdatePortalMutation()

    const [slug, setSlug] = useState(portal?.slug ?? '')
    const [slugTouched, setSlugTouched] = useState(editing)
    const [branding, setBranding] = useState<PortalBranding>(
        portal?.branding ?? DEFAULT_PORTAL_BRANDING,
    )
    const [reports, setReports] = useState<string[]>(portal?.reports ?? DEFAULT_PORTAL_REPORTS)
    const [active, setActive] = useState(portal ? portal.status === 'active' : true)

    const set = <K extends keyof PortalBranding>(k: K, v: PortalBranding[K]) =>
        setBranding(b => ({ ...b, [k]: v }))

    const logoError = !!branding.logoUrl && !isHttpsUrl(branding.logoUrl)
    const emailError = !!branding.supportEmail && !isEmail(branding.supportEmail.trim())
    const nameError = !branding.productName.trim() || branding.productName.trim().length > 60
    const slugError = !editing && !isValidPortalSlug(slug)
    const busy = createState.isLoading || updateState.isLoading
    const valid = !logoError && !emailError && !nameError && !slugError && reports.length > 0

    const save = async () => {
        const body: PortalBranding = {
            ...branding,
            productName: branding.productName.trim(),
            logoUrl: branding.logoUrl?.trim() || null,
            supportEmail: branding.supportEmail?.trim() || null,
        }
        try {
            const saved = portal
                ? await updatePortal({
                      projectId,
                      id: portal.id,
                      branding: body,
                      reports,
                      status: active ? 'active' : 'disabled',
                  }).unwrap()
                : await createPortal({ projectId, slug, branding: body, reports }).unwrap()
            if (body.hidePoweredBy && saved.powered_by) {
                dispatch(
                    showNotification({
                        message: 'Saved. "Powered by YOM" stays visible on your current plan.',
                        severity: 'info',
                    }),
                )
            }
            onSaved(saved)
        } catch {
            // shown below
        }
    }

    return (
        <Dialog open onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {portal ? `Edit portal · ${portal.slug}` : 'New client portal'}
            </DialogTitle>
            <DialogContent>
                <ApiErrorAlert error={createState.error ?? updateState.error} sx={{ mb: 2 }} />
                <Grid container spacing={3} sx={{ mt: 0 }}>
                    <Grid
                        size={{ xs: 12, md: 7 }}
                        sx={{ display: 'grid', gap: 2, alignContent: 'start' }}
                    >
                        <TextField
                            size="small"
                            label="Product name"
                            required
                            value={branding.productName}
                            error={nameError}
                            helperText="Shown in the portal header, page title and emails (max 60)."
                            onChange={e => {
                                set('productName', e.target.value)
                                if (!slugTouched) {
                                    setSlug(slugify(e.target.value))
                                }
                            }}
                            sx={{ mt: 1 }}
                        />
                        <TextField
                            size="small"
                            label="Portal address (slug)"
                            required
                            disabled={editing}
                            value={slug}
                            error={slugError && slug !== ''}
                            onChange={e => {
                                setSlugTouched(true)
                                setSlug(e.target.value.toLowerCase())
                            }}
                            helperText={
                                editing
                                    ? 'The slug cannot be changed.'
                                    : '3–48 characters: a–z, 0–9 and dashes. Clients sign in at /portal/<slug>.'
                            }
                        />
                        <TextField
                            size="small"
                            label="Logo URL (https)"
                            placeholder="https://example.com/logo.svg"
                            value={branding.logoUrl ?? ''}
                            error={logoError}
                            helperText={
                                logoError
                                    ? 'Must be an https:// URL'
                                    : 'Leave empty to show the product name.'
                            }
                            onChange={e => set('logoUrl', e.target.value || null)}
                        />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <ColorField
                                label="Primary color"
                                value={branding.primaryColor}
                                onChange={v => set('primaryColor', v)}
                            />
                            <ColorField
                                label="Accent color"
                                value={branding.accentColor}
                                onChange={v => set('accentColor', v)}
                            />
                        </Box>
                        <TextField
                            size="small"
                            label="Support email"
                            placeholder="support@example.com"
                            value={branding.supportEmail ?? ''}
                            error={emailError}
                            helperText="Shown to clients and used as reply-to on their emails."
                            onChange={e => set('supportEmail', e.target.value || null)}
                        />
                        <Box>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={canRemoveBranding && branding.hidePoweredBy}
                                        disabled={!canRemoveBranding}
                                        onChange={e => set('hidePoweredBy', e.target.checked)}
                                    />
                                }
                                label='Hide "Powered by YOM"'
                            />
                            {!canRemoveBranding && (
                                <FormHelperText sx={{ mt: -0.5 }}>
                                    Available on paid plans — the footer stays on the free plan.
                                </FormHelperText>
                            )}
                        </Box>
                        {editing && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={active}
                                        onChange={e => setActive(e.target.checked)}
                                    />
                                }
                                label={
                                    active
                                        ? 'Active — clients can sign in'
                                        : 'Disabled — sign-in is blocked'
                                }
                            />
                        )}
                    </Grid>
                    <Grid
                        size={{ xs: 12, md: 5 }}
                        sx={{ display: 'grid', gap: 2, alignContent: 'start' }}
                    >
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                Preview
                            </Typography>
                            <BrandPreview branding={branding} />
                        </Box>
                        <Box>
                            <FormLabel component="legend" sx={{ fontSize: 14 }}>
                                Reports clients can see
                            </FormLabel>
                            <FormGroup>
                                {PORTAL_REPORTS.map(r => (
                                    <FormControlLabel
                                        key={r}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={reports.includes(r)}
                                                onChange={() =>
                                                    setReports(toggleReport(reports, r))
                                                }
                                            />
                                        }
                                        label={
                                            <Typography variant="body2">
                                                {PORTAL_REPORT_LABELS[r]}
                                            </Typography>
                                        }
                                    />
                                ))}
                            </FormGroup>
                            <FormHelperText>At least one report stays selected.</FormHelperText>
                        </Box>
                    </Grid>
                </Grid>
                {!editing && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        After creating the portal, invite client logins and (on paid plans) connect
                        a custom domain.
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    disabled={!valid || busy}
                    onClick={() => {
                        void save()
                    }}
                >
                    {editing ? 'Save' : 'Create portal'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
